export type APIErrorSeverity = "high" | "medium" | "low";

type APIErrorLink = {
  label: string;
  url: string;
};

export type APIError = {
  severity: APIErrorSeverity;
  slug: string;
  status_code: number;
  title: string;
  description?: string;
  // Guidance links — shown in v1, on any job. These are NOT call-to-actions, just help:
  docsLink?: APIErrorLink;     // "View docs" / "View Terms"
  supportLink?: APIErrorLink;  // "Contact support" (mailto)
  // Real product call-to-action (e.g. "Apply render=true", "Upgrade plan"). Deferred —
  // not rendered in v1, and only ever shown on the latest job. Unpopulated for now.
  action?: APIErrorLink;
};

const SUPPORT_LINK: APIErrorLink = { label: "Contact support", url: "mailto:support@scraperapi.com" };

// Internal error codes we deliberately don't surface. Keyed by the backend's emitted slug
// (cookieUnavailablie is misspelled upstream).
export const HIDDEN_TERMINATION_SLUGS = new Set([ 'cookieUnavailable', 'cookieUnavailablie' ]);

export const APIErrorList: Record<string, APIError> = {
  // --- High — hard failure errors ---
  noAPIKey: {
    severity: "high",
    slug: "noAPIKey",
    status_code: 401,
    title: "Add your API key to continue",
    description: "We couldn't find an API key on this request.  Check it in your dashboard and try again.",
    docsLink: { label: "View docs", url: "https://docs.scraperapi.com/synchronous-apis/overview" },
  },
  unauthorized: {
    severity: "high",
    slug: "unauthorized",
    status_code: 400,
    title: "This feature isn't on your plan",
    description: "Your current plan doesn't include this feature. Contact support to enable it.",
    supportLink: SUPPORT_LINK,
  },
  needsPaidPlan: {
    severity: "high",
    slug: "needsPaidPlan",
    status_code: 403,
    title: "This domain needs a paid plan",
    description: "Upgrade to scrape it. Your existing jobs aren't affected.",
  },
  multipleUsersFromSameIp: {
    severity: "high",
    slug: "multipleUsersFromSameIp",
    status_code: 499,
    title: "Multiple users detected on your IP",
    description: "This is only allowed on a paid subscription. Contact support if this is unexpected.",
    supportLink: SUPPORT_LINK,
  },
  bannedDomain: {
    severity: "high",
    slug: "bannedDomain",
    status_code: 403,
    title: "This URL can't be scraped",
    description: "See our Terms of Use for the list of prohibited targets.",
    docsLink: { label: "View Terms", url: "https://www.scraperapi.com/terms/" },
  },
  requestLimit: {
    severity: "high",
    slug: "requestLimit",
    status_code: 403,
    title: "You've used all your API credits",
    description: "Upgrade your plan or enable overages to keep scraping. Your quota resets on the 1st of each month.",
  },
  failedScrape: {
    severity: "high",
    slug: "failedScrape",
    status_code: 500,
    title: "Some requests couldn't be scraped",
    description: "A number of requests in this job didn't complete. This can happen with protected domains or targets.",
  },
  failedScrapeBadHeaders: {
    severity: "high",
    slug: "failedScrapeBadHeaders",
    status_code: 500,
    title: "Custom headers blocked the scrape",
    description: "Retry without keep_headers or force_headers — our bypasses handle this domain.",
    docsLink: { label: "View docs", url: "https://docs.scraperapi.com/control-and-optimization/sending-custom-headers" },
  },
  scrapeLimitReached: {
    severity: "high",
    slug: "scrapeLimitReached",
    status_code: 500,
    title: "We couldn’t complete your request",
    description: "The page you’re trying to scrape is too large, which isn’t supported. Try reducing the page size.", // TODO: removed (50 MB)
    supportLink: SUPPORT_LINK,
  },
  frozenAccount: {
    severity: "high",
    slug: "frozenAccount",
    status_code: 403,
    title: "Your account is frozen",
    description: "Contact support to reactivate your account and resume scraping.",
    supportLink: SUPPORT_LINK,
  },
  // Keyed to the backend slug, which is misspelled (missing the trailing "s"). Re-sync if the backend fixes it.
  payAsYouGoUnpaidInvoice: {
    severity: "high",
    slug: "payAsYouGoUnpaidInvoice",
    status_code: 403,
    title: "Unpaid invoices are blocking your account",
    description: "Pay your outstanding invoices to keep scraping with pay-as-you-go credits.",
    supportLink: SUPPORT_LINK,
  },
  // cookieUnavailable: intentionally excluded — internal error code, should not be exposed to users.

  // --- Medium — invalid requests ---
  needsPremium: {
    severity: "medium",
    slug: "needsPremium",
    status_code: 403,
    title: "Premium proxies aren't on your plan",
    description: "Upgrade to access them, or remove premium=true to retry on standard proxies.",
    docsLink: { label: "View docs", url: "https://docs.scraperapi.com/control-and-optimization/premium-residential-mobile-proxy-pools" },
  },
  needsUltraPremium: {
    severity: "medium",
    slug: "needsUltraPremium",
    status_code: 403,
    title: "This domain needs Ultra Premium proxies",
    description: "Add ultra_premium=true to retry (30 credits per request) or pick a different target.",
    docsLink: { label: "View docs", url: "https://docs.scraperapi.com/control-and-optimization/premium-residential-mobile-proxy-pools" },
  },
  needsRender: {
    severity: "medium",
    slug: "needsRender",
    status_code: 403,
    title: "This domain needs JavaScript rendering",
    description: "Add render=true to retry (10 credits per request).",
    docsLink: { label: "View docs", url: "https://docs.scraperapi.com/getting-started/quick-start/credits-and-requests-costs" },
  },
  needsGeo: {
    severity: "medium",
    slug: "needsGeo",
    status_code: 403,
    title: "Geotargeting isn't on your plan",
    description: "Upgrade your plan, or contact support for custom geotargeting.",
    supportLink: SUPPORT_LINK,
  },
  needsAutoparse: {
    severity: "medium",
    slug: "needsAutoparse",
    status_code: 403,
    title: "Autoparse isn't enabled on your plan",
    description: "Contact support to turn on structured-data parsing.",
    supportLink: SUPPORT_LINK,
  },
  premiumGeo: {
    severity: "medium",
    slug: "premiumGeo",
    status_code: 403,
    title: "This country needs Premium proxies",
    description: "Add premium=true to target it from a residential IP.",
    docsLink: { label: "View docs", url: "https://docs.scraperapi.com/control-and-optimization/geotargeting/premium-geo" },
  },
  costsMoreThanMaxCost: {
    severity: "medium",
    slug: "costsMoreThanMaxCost",
    status_code: 403,
    title: "This request exceeds your max_cost",
    description: "Raise the limit or simplify the request. View the cost breakdown in the API Playground.",
  },
  burstLimit: {
    severity: "medium",
    slug: "burstLimit",
    status_code: 429,
    title: "You're sending requests too fast",
    description: "Limit is 1,000 requests per second. Slow down and we'll resume.",
  },
  rateLimit: {
    severity: "medium",
    slug: "rateLimit",
    status_code: 429,
    title: "Too many simultaneous requests",
    description: "Lower your concurrency to your plan's limit and try again.",
  },
  preconditionFailed: {
    severity: "medium",
    slug: "preconditionFailed",
    status_code: 412,
    title: "This request couldn't be completed",
    description: "A precondition on the request failed. Check your request parameters and try again.",
  },

  // --- Low — invalid requests ---
  invalidUrl: {
    severity: "low",
    slug: "invalidUrl",
    status_code: 400,
    title: "Enter a valid URL starting with https://",
  },
  invalidTarget: {
    severity: "low",
    slug: "invalidTarget",
    status_code: 400,
    title: "This target host isn't supported",
    description: "Pick from the supported list in the docs.",
  },
  invalidTld: {
    severity: "low",
    slug: "invalidTld",
    status_code: 400,
    title: "This domain extension isn't supported",
    description: "Check the docs for the list of valid TLDs.",
  },
  malformedRequest: {
    severity: "low",
    slug: "malformedRequest",
    status_code: 400,
    title: "This request is malformed",
    description: "One or more parameters aren't in the expected format. Check the request and try again.",
    supportLink: SUPPORT_LINK,
  },
  invalidApiParam: {
    severity: "low",
    slug: "invalidApiParam",
    status_code: 400,
    title: "An API parameter is invalid",
    description: "Check the highlighted parameter against the docs.",
    docsLink: { label: "View docs", url: "https://docs.scraperapi.com/control-and-optimization/supported-parameters" },
  },
  invalidMaxCost: {
    severity: "low",
    slug: "invalidMaxCost",
    status_code: 400,
    title: "Enter a max_cost greater than 0, or leave it blank",
  },
  invalidMethod: {
    severity: "low",
    slug: "invalidMethod",
    status_code: 401,
    title: "This HTTP method isn't supported",
    description: "Use HEAD, GET, POST, or PUT.",
  },
  invalidInstructionSet: {
    severity: "low",
    slug: "invalidInstructionSet",
    status_code: 400,
    title: "This instruction set isn't valid",
    description: "Check the JSON syntax and supported instructions.",
    docsLink: { label: "View docs", url: "https://docs.scraperapi.com/javascript-rendering/rendering-instruction-set#supported-instructions" },
  },
  malformedProxyRequest: {
    severity: "low",
    slug: "malformedProxyRequest",
    status_code: 400,
    title: "This proxy request is missing fields",
    description: "Check that proxy_id and proxy_hash are included.",
  },
  scrapeSelf: {
    severity: "low",
    slug: "scrapeSelf",
    status_code: 400,
    title: "You can't scrape scraperapi.com through ScraperAPI",
    description: "Point your request at a different target URL.",
    docsLink: { label: "View docs", url: "https://docs.scraperapi.com/synchronous-apis/using-the-api-endpoint" },
    supportLink: SUPPORT_LINK,
  },
  unsupportedFileType: {
    severity: "low",
    slug: "unsupportedFileType",
    status_code: 400,
    title: "This file type isn't supported",
    description: "This file type isn't supported. See the docs for the list of supported response types",
  },
  noUltrapremiumRendering: {
    severity: "low",
    slug: "noUltrapremiumRendering",
    status_code: 400,
    title: "ultra_premium and render can't be combined",
    description: "Pick one, or contact support to enable both on your account.",
    supportLink: SUPPORT_LINK,
  },
};
